// What the user may do in the current company, as the host sets it on window.accessContext.
// Pure (no imports) for unit tests; the API enforces every rule again, this only shapes the screens.
export interface AccessContext {
  mode: "member" | "support";
  isPlatformStaff: boolean;
  isPlatformOwnerCompany: boolean;
  canEditRoles: boolean;
  canManageStaffAccess: boolean;
  homeCompanyId: string;
  currentCompanyId: string;
  currentCompanyName: string;
}

export const DEFAULT_ACCESS_CONTEXT: AccessContext = {
  mode: "member",
  isPlatformStaff: false,
  isPlatformOwnerCompany: false,
  canEditRoles: false,
  canManageStaffAccess: false,
  homeCompanyId: "",
  currentCompanyId: "",
  currentCompanyName: "",
};

const text = (value: unknown) => (typeof value === "string" ? value : "");

// Deny by default: only a literal true grants a flag, and both edit rights also need a member
// (never a support session) of a platform-owner company.
export const normalizeAccessContext = (raw: unknown): AccessContext => {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_ACCESS_CONTEXT };
  const value = raw as Record<string, unknown>;
  const mode = value.mode === "support" ? "support" : "member";
  const isPlatformOwnerCompany = value.isPlatformOwnerCompany === true;
  const memberOfOwner = mode === "member" && isPlatformOwnerCompany;
  return {
    mode,
    isPlatformStaff: value.isPlatformStaff === true,
    isPlatformOwnerCompany,
    canEditRoles: memberOfOwner && value.canEditRoles === true,
    canManageStaffAccess: memberOfOwner && value.canManageStaffAccess === true,
    homeCompanyId: text(value.homeCompanyId),
    currentCompanyId: text(value.currentCompanyId),
    currentCompanyName: text(value.currentCompanyName),
  };
};

export type RoleTab = "system" | "device" | "support";

/** The Roles screen's tabs; 'InnovX support' only for role editors inside InnovX. */
export const roleTabsFor = (ctx: AccessContext): RoleTab[] =>
  ctx.isPlatformOwnerCompany && ctx.canEditRoles ? ["system", "device", "support"] : ["system", "device"];

/** A ?tab= value the viewer may not open falls back to Web roles. */
export const resolveRoleTab = (requested: unknown, ctx: AccessContext): RoleTab => {
  const tabs = roleTabsFor(ctx);
  return tabs.includes(requested as RoleTab) ? (requested as RoleTab) : "system";
};

/** Web and support roles are edited only by InnovX role editors, and never a role the API marks read-only. */
export const canEditWebRole = (ctx: AccessContext, role?: { canEdit?: unknown } | null): boolean =>
  ctx.canEditRoles && (!role || role.canEdit !== false);

/** The user form's 'Customer access' field: InnovX staff-access managers, on an existing user only. */
export const showCustomerAccessField = (ctx: AccessContext, userId: unknown): boolean =>
  ctx.isPlatformOwnerCompany &&
  ctx.canManageStaffAccess &&
  typeof userId === "string" &&
  userId.trim() !== "" &&
  userId !== "new";
